import { FormGroup } from '@angular/forms';
import { QuestionnaireRequest, S3Object } from 'src/API';
import { compareDateStrings } from 'src/app/core/api/date.util';
import { safeJsonParse } from 'src/app/core/api/general.util';
import {
  ConsentFormStatus,
  FormFieldConfig,
  FormTemplateSchema,
  FormTemplateSchemaType
} from 'src/app/form-template/form-template-model';

export interface ConsentClinicianPrefillField {
  groupKey: string;
  fieldKey: string;
  /** Segments under the group when nested in acknowledgement (e.g. ['ack1']). */
  nestPath?: string[];
  config: FormFieldConfig;
}

export interface ConsentClinicBranding {
  name: string;
  logo?: S3Object | null;
}

/** Single field value stored in clinician prefill (text, or checkbox option map). */
export type ClinicianPrefillFieldValue =
  | string
  | number
  | boolean
  | null
  | { [controlKey: string]: string | number | boolean | null };

/** Nested patch applied to FormTemplateInputFormGroup (group → field → value, possibly nested). */
export type ClinicianPrefillPatch = {
  [groupKey: string]: any;
};

/** Parsed QuestionnaireRequest.config JSON used by consent + questionnaire flows. */
export interface QuestionnaireRequestConfig {
  schema?: FormTemplateSchema;
  questionnaireExpiry?: number;
  clinicBranding?: ConsentClinicBranding;
  clinicianPrefill?: ClinicianPrefillPatch;
  consentStatus?: ConsentFormStatus;
}

export type QuestionnaireWithConfig =
  | Partial<Pick<QuestionnaireRequest, 'config'>>
  | { config?: string | QuestionnaireRequestConfig | null }
  | null
  | undefined;

export function parseQuestionnaireConfig(
  questionnaire: QuestionnaireWithConfig
): QuestionnaireRequestConfig {
  if (!questionnaire || questionnaire.config == null) {
    return {};
  }
  if (typeof questionnaire.config === 'object') {
    return questionnaire.config;
  }
  return (safeJsonParse(questionnaire.config) as QuestionnaireRequestConfig) || {};
}

export function isConsentQuestionnaire(questionnaire: QuestionnaireWithConfig): boolean {
  const questionnaireConfig = parseQuestionnaireConfig(questionnaire);
  return !!(
    questionnaireConfig &&
    questionnaireConfig.schema &&
    questionnaireConfig.schema.type === FormTemplateSchemaType.consent
  );
}

export function getConsentFormStatus(
  questionnaire: QuestionnaireWithConfig
): ConsentFormStatus | null {
  const questionnaireConfig = parseQuestionnaireConfig(questionnaire);
  if (!questionnaireConfig || !questionnaireConfig.consentStatus) {
    return null;
  }
  if (
    questionnaireConfig.consentStatus === 'accepted' ||
    questionnaireConfig.consentStatus === 'rejected'
  ) {
    return questionnaireConfig.consentStatus;
  }
  return null;
}

export function getConsentSchemaId(questionnaire: QuestionnaireWithConfig): string | null {
  const questionnaireConfig = parseQuestionnaireConfig(questionnaire);
  return (
    (questionnaireConfig &&
      questionnaireConfig.schema &&
      questionnaireConfig.schema.id) ||
    null
  );
}

/** A completed consent form, reduced to what callers need to reason about its outcome. */
export interface ConsentFormResponse {
  /** config.schema.id — null for custom templates saved without one. */
  schemaId: string | null;
  name: string;
  status: ConsentFormStatus;
  completedAt: string;
  questionnaire: QuestionnaireRequest;
}

// Custom templates can be saved without a schema id, so the name is the only stable identity left.
function consentFormIdentity(schemaId: string | null, name: string): string {
  return schemaId ? `id:${schemaId}` : `name:${name}`;
}

export function consentFormResponseMatchesForm(
  response: ConsentFormResponse,
  formSchemaId: string | null,
  formName: string
): boolean {
  if (response.schemaId) {
    return response.schemaId === formSchemaId;
  }
  return !!response.name && response.name === formName;
}

function toConsentFormResponse(questionnaire: QuestionnaireRequest): ConsentFormResponse {
  if (!questionnaire || !questionnaire.completedAt || !isConsentQuestionnaire(questionnaire)) {
    return null;
  }
  const status = getConsentFormStatus(questionnaire);
  if (!status) {
    return null;
  }
  const questionnaireConfig = parseQuestionnaireConfig(questionnaire);
  return {
    schemaId: getConsentSchemaId(questionnaire),
    name: (questionnaireConfig.schema && questionnaireConfig.schema.name) || '',
    status,
    completedAt: questionnaire.completedAt,
    questionnaire
  };
}

/**
 * The most recent completed response to each consent form, newest first. Only completed
 * responses count: a newer copy that was sent but never filled in says nothing about the
 * patient's answer, so it must not hide an earlier decline or acceptance.
 */
export function getLatestConsentFormResponses(
  questionnaires: QuestionnaireRequest[]
): ConsentFormResponse[] {
  const latestByForm: { [identity: string]: ConsentFormResponse } = {};
  (questionnaires || []).forEach(questionnaire => {
    const response = toConsentFormResponse(questionnaire);
    if (!response) {
      return;
    }
    const identity = consentFormIdentity(response.schemaId, response.name);
    const current = latestByForm[identity];
    if (!current || compareDateStrings(response.completedAt, current.completedAt) > 0) {
      latestByForm[identity] = response;
    }
  });
  return Object.keys(latestByForm)
    .map(identity => latestByForm[identity])
    .sort((first, second) => compareDateStrings(second.completedAt, first.completedAt));
}

export function getDeclinedConsentFormResponses(
  questionnaires: QuestionnaireRequest[]
): ConsentFormResponse[] {
  return getLatestConsentFormResponses(questionnaires).filter(
    response => response.status === 'rejected'
  );
}

/** Dot path for nested clinician fields (e.g. acknowledgements.ack1.practitionerName). */
export function clinicianFieldDotPath(clinicianField: ConsentClinicianPrefillField): string {
  const nestSegments = clinicianField.nestPath || [];
  return [clinicianField.groupKey, ...nestSegments, clinicianField.fieldKey].join('.');
}

function sortFormFieldKeys(formFields: { [key: string]: FormFieldConfig }): string[] {
  return Object.keys(formFields).sort((fieldKeyA, fieldKeyB) => {
    const sortOrderA = formFields[fieldKeyA].sort != null ? formFields[fieldKeyA].sort : 0;
    const sortOrderB = formFields[fieldKeyB].sort != null ? formFields[fieldKeyB].sort : 0;
    return sortOrderA - sortOrderB;
  });
}

function collectClinicianOnlyFromFields(
  groupKey: string,
  formFields: { [key: string]: FormFieldConfig },
  nestPath: string[],
  clinicianOnlyFields: ConsentClinicianPrefillField[]
): void {
  sortFormFieldKeys(formFields).forEach(fieldKey => {
    const fieldConfig = formFields[fieldKey];
    if (!fieldConfig) {
      return;
    }
    if (fieldConfig.clinicianOnly) {
      clinicianOnlyFields.push({
        groupKey,
        fieldKey,
        nestPath: nestPath.length > 0 ? nestPath.slice() : undefined,
        config: fieldConfig
      });
    }
    if (
      fieldConfig.typeConfig &&
      fieldConfig.typeConfig.type === 'acknowledgement' &&
      fieldConfig.typeConfig.formFields
    ) {
      collectClinicianOnlyFromFields(
        groupKey,
        fieldConfig.typeConfig.formFields,
        nestPath.concat(fieldKey),
        clinicianOnlyFields
      );
    }
  });
}

/** Collect fields marked clinicianOnly from a consent schema (stable sort). */
export function getClinicianOnlyFields(schema: FormTemplateSchema): ConsentClinicianPrefillField[] {
  if (!schema || !schema.templateGroups) {
    return [];
  }
  const clinicianOnlyFields: ConsentClinicianPrefillField[] = [];
  Object.keys(schema.templateGroups)
    .sort((groupKeyA, groupKeyB) => {
      const sortOrderA =
        schema.templateGroups[groupKeyA].sort != null ? schema.templateGroups[groupKeyA].sort : 0;
      const sortOrderB =
        schema.templateGroups[groupKeyB].sort != null ? schema.templateGroups[groupKeyB].sort : 0;
      return sortOrderA - sortOrderB;
    })
    .forEach(groupKey => {
      const templateGroup = schema.templateGroups[groupKey];
      if (!templateGroup || !templateGroup.formFields) {
        return;
      }
      collectClinicianOnlyFromFields(groupKey, templateGroup.formFields, [], clinicianOnlyFields);
    });
  return clinicianOnlyFields;
}

/**
 * True when a consent form carries fields staff must fill in at send time (clinicianOnly), so the
 * send cannot proceed without first collecting a clinician prefill. Only consent schemas qualify:
 * a clinicianOnly field on any other template type is not part of the prefill flow.
 */
export function consentFormRequiresClinicianInput(schema: FormTemplateSchema): boolean {
  return (
    !!schema &&
    schema.type === FormTemplateSchemaType.consent &&
    getClinicianOnlyFields(schema).length > 0
  );
}

/** Build nested patch object for FormTemplateInputFormGroup from dialog flat values. */
export function buildClinicianPrefillPatch(
  clinicianOnlyFields: ConsentClinicianPrefillField[],
  fieldValuesByNestedPath: { [nestedFieldPath: string]: ClinicianPrefillFieldValue }
): ClinicianPrefillPatch {
  const clinicianPrefillPatch: ClinicianPrefillPatch = {};
  clinicianOnlyFields.forEach(clinicianField => {
    const nestedFieldPath = clinicianFieldDotPath(clinicianField);
    if (!clinicianPrefillPatch[clinicianField.groupKey]) {
      clinicianPrefillPatch[clinicianField.groupKey] = {};
    }
    let target = clinicianPrefillPatch[clinicianField.groupKey];
    (clinicianField.nestPath || []).forEach(segment => {
      if (!target[segment] || typeof target[segment] !== 'object') {
        target[segment] = {};
      }
      target = target[segment];
    });
    target[clinicianField.fieldKey] = fieldValuesByNestedPath[nestedFieldPath];
  });
  return clinicianPrefillPatch;
}

/** Disable clinicianOnly controls after prefill so patients cannot edit them. */
export function disableClinicianOnlyControls(
  formTemplateInputFormGroup: FormGroup,
  schema: FormTemplateSchema
): void {
  const clinicianOnlyFields = getClinicianOnlyFields(schema);
  clinicianOnlyFields.forEach(clinicianField => {
    const controlPath = [
      clinicianField.groupKey,
      ...(clinicianField.nestPath || []),
      clinicianField.fieldKey
    ];
    const fieldControl = formTemplateInputFormGroup.get(controlPath);
    if (fieldControl) {
      fieldControl.disable({ emitEvent: false });
    }
  });
}

export interface ConsentSchemaFieldPath {
  groupKey: string;
  fieldKey: string;
  nestPath?: string[];
  required?: boolean;
}

function collectLockedDatesFromFields(
  groupKey: string,
  formFields: { [key: string]: FormFieldConfig },
  nestPath: string[],
  lockedDateFields: ConsentSchemaFieldPath[]
): void {
  Object.keys(formFields).forEach(fieldKey => {
    const fieldConfig = formFields[fieldKey];
    if (!fieldConfig || !fieldConfig.typeConfig) {
      return;
    }
    if (fieldConfig.typeConfig.type === 'date' && fieldConfig.typeConfig.lockedToToday) {
      lockedDateFields.push({
        groupKey,
        fieldKey,
        nestPath: nestPath.length > 0 ? nestPath.slice() : undefined,
        required: !!fieldConfig.required
      });
    }
    if (fieldConfig.typeConfig.type === 'acknowledgement' && fieldConfig.typeConfig.formFields) {
      collectLockedDatesFromFields(
        groupKey,
        fieldConfig.typeConfig.formFields,
        nestPath.concat(fieldKey),
        lockedDateFields
      );
    }
  });
}

/** Date fields locked to today (disabled on patient form; still must be persisted). */
export function getLockedToTodayDateFields(schema: FormTemplateSchema): ConsentSchemaFieldPath[] {
  if (!schema || !schema.templateGroups) {
    return [];
  }
  const lockedDateFields: ConsentSchemaFieldPath[] = [];
  Object.keys(schema.templateGroups).forEach(groupKey => {
    const templateGroup = schema.templateGroups[groupKey];
    if (!templateGroup || !templateGroup.formFields) {
      return;
    }
    collectLockedDatesFromFields(groupKey, templateGroup.formFields, [], lockedDateFields);
  });
  return lockedDateFields;
}

function getNestedGroupAnswers(
  answers: { [key: string]: any },
  groupKey: string,
  nestPath?: string[]
): { [key: string]: any } | null {
  if (!answers || !answers[groupKey] || typeof answers[groupKey] !== 'object') {
    return null;
  }
  let cursor: any = answers[groupKey];
  for (const segment of nestPath || []) {
    if (!cursor || typeof cursor !== 'object' || cursor[segment] === undefined) {
      return null;
    }
    cursor = cursor[segment];
  }
  return cursor && typeof cursor === 'object' ? cursor : null;
}

/** True when optional witness name or signature was filled. */
export function hasConsentWitnessAnswers(groupAnswers: { [key: string]: any } | null): boolean {
  if (!groupAnswers) {
    return false;
  }
  if (hasConsentPdfScalarAnswer(groupAnswers.witnessPrintName)) {
    return true;
  }
  return hasConsentSignatureAnswer(groupAnswers.witnessSignature);
}

export function hasConsentSignatureAnswer(value: any): boolean {
  if (!value || !value.signatureType) {
    return false;
  }
  if (value.signatureType === 'drawn' && value.drawnSignature) {
    return true;
  }
  if (value.signatureType === 'typed' && value.typedSignature) {
    return true;
  }
  if (value.signatureType === 'upload' && value.uploadedSignature) {
    return true;
  }
  return false;
}

/**
 * Required locked dates always persist. Optional ones (witnessSignDate) only persist
 * when witness name/signature were actually filled — otherwise a blank witness still
 * gets today's date from lockedToToday + getRawValue merge.
 */
function shouldPersistLockedToTodayDate(
  dateField: ConsentSchemaFieldPath,
  patientAnswers: { [key: string]: any },
  rawAnswers: { [key: string]: any }
): boolean {
  if (dateField.required) {
    return true;
  }
  if (dateField.fieldKey !== 'witnessSignDate') {
    return false;
  }
  const patientGroup = getNestedGroupAnswers(
    patientAnswers,
    dateField.groupKey,
    dateField.nestPath
  );
  const rawGroup = getNestedGroupAnswers(rawAnswers, dateField.groupKey, dateField.nestPath);
  return hasConsentWitnessAnswers(patientGroup) || hasConsentWitnessAnswers(rawGroup);
}

function mergeNestedFieldFromRaw(
  mergedAnswers: { [key: string]: any },
  rawAnswers: { [key: string]: any },
  groupKey: string,
  fieldKey: string,
  nestPath?: string[]
): void {
  const pathSegments = [groupKey, ...(nestPath || []), fieldKey];
  let rawCursor: any = rawAnswers;
  for (const segment of pathSegments) {
    if (!rawCursor || rawCursor[segment] === undefined) {
      return;
    }
    rawCursor = rawCursor[segment];
  }

  if (!mergedAnswers[groupKey] || typeof mergedAnswers[groupKey] !== 'object') {
    mergedAnswers[groupKey] = {};
  }
  let mergedCursor = mergedAnswers[groupKey];
  (nestPath || []).forEach(segment => {
    if (!mergedCursor[segment] || typeof mergedCursor[segment] !== 'object') {
      mergedCursor[segment] = {};
    }
    mergedCursor = mergedCursor[segment];
  });
  mergedCursor[fieldKey] = rawCursor;
}

/**
 * Consent submit values: patient-visible answers from `.value` (disabled trigger subfields
 * and legalText omitted), plus disabled fields that must still persist:
 * clinicianOnly prefill and lockedToToday dates.
 * Avoids persisting hidden Yes-detail text or null placeholders when parent radio is No.
 */
export function buildConsentSubmitFormValues(
  formTemplateInputFormGroup: FormGroup,
  schema: FormTemplateSchema
): { [key: string]: any } {
  const patientAnswers = formTemplateInputFormGroup.value;
  const rawAnswers = formTemplateInputFormGroup.getRawValue();
  const clinicianOnlyFields = getClinicianOnlyFields(schema);
  const lockedToTodayDateFields = getLockedToTodayDateFields(schema);

  if (clinicianOnlyFields.length === 0 && lockedToTodayDateFields.length === 0) {
    return patientAnswers;
  }

  const mergedAnswers = { ...patientAnswers };
  clinicianOnlyFields.forEach(clinicianField => {
    mergeNestedFieldFromRaw(
      mergedAnswers,
      rawAnswers,
      clinicianField.groupKey,
      clinicianField.fieldKey,
      clinicianField.nestPath
    );
  });
  lockedToTodayDateFields.forEach(dateField => {
    if (!shouldPersistLockedToTodayDate(dateField, patientAnswers, rawAnswers)) {
      return;
    }
    mergeNestedFieldFromRaw(
      mergedAnswers,
      rawAnswers,
      dateField.groupKey,
      dateField.fieldKey,
      dateField.nestPath
    );
  });
  return mergedAnswers;
}

/** True when a consent PDF should show the italic "Not Answered" placeholder. */
export function shouldShowConsentPdfNotAnswered(
  isConsentForm: boolean,
  formatForPDF: boolean,
  hasAnswer: boolean
): boolean {
  return !!isConsentForm && !!formatForPDF && !hasAnswer;
}

/** Scalar consent answers (radio/text/initials/date) count as answered when non-empty. */
export function hasConsentPdfScalarAnswer(value: any): boolean {
  return !(value === null || value === undefined || value === '');
}
